import { FormattedMessage } from 'react-intl';
import { useCallback, useMemo } from 'react';
import { TileContent } from 'components/Layout/PageContainer';
import { Measure } from 'services/model/model.service';
import { MarketplaceInfo } from '../model';

interface StatusRowProps {
  defaultLabel: string;
  marketplaces: MarketplaceInfo[];
  translationKey: string;
}

function StatusRow({ defaultLabel, marketplaces, translationKey }: StatusRowProps) {
  return (
    <>
      <div className="flex items-center">
        <span className="text-md mr-2 font-bold min-w-[120px]">
          <FormattedMessage id={translationKey} defaultMessage={defaultLabel} />
        </span>
        <span className="px-2 py-1 bg-gray-100 rounded-full text-sm font-bold">{marketplaces.length}</span>
      </div>
      <div className="mt-2">
        {marketplaces?.map((marketplace, index) => (
          <span key={marketplace.name} className="mr-1">
            {index === marketplaces.length - 1 ? marketplace.name : `${marketplace.name} |`}
          </span>
        ))}
      </div>
    </>
  );
}

interface ActiveMarketplacesProps {
  marketplaces: MarketplaceInfo[];
  isComparisonActive: boolean;
  qtyMeasure: Measure;
}

export function ActiveMarketplaces({ marketplaces, isComparisonActive, qtyMeasure }: ActiveMarketplacesProps) {
  const getFilteredMarketplaces = useCallback(
    (filter: (value: number, comparisonValue: number) => boolean) =>
      marketplaces.filter(marketplace =>
        filter(qtyMeasure.getValue(marketplace.entry.values), qtyMeasure.getValue(marketplace.entry.compareValues))
      ),
    [marketplaces, qtyMeasure]
  );

  const activeMarketplaces = useMemo(
    () => getFilteredMarketplaces(value => value > 0).length,
    [getFilteredMarketplaces]
  );
  const launchedMarketplaces = useMemo(
    () => getFilteredMarketplaces((value, compareValue) => value > 0 && compareValue === 0),
    [getFilteredMarketplaces]
  );

  const discontinuedMarketplaces = useMemo(
    () => getFilteredMarketplaces((value, compareValue) => value === 0 && compareValue > 0),
    [getFilteredMarketplaces]
  );

  return (
    <TileContent className="flex items-center">
      <div className="flex w-full">
        <div className="flex items-center justify-center w-2/5 text-4xl font-bold pr-6">{activeMarketplaces}</div>

        <div className="w-px bg-gray-200 min-h-full" />

        {isComparisonActive && (
          <div className="flex flex-col gap-6 pl-10 w-3/5">
            <div>
              <StatusRow
                defaultLabel="Launched"
                marketplaces={launchedMarketplaces}
                translationKey="insights.country_tab.active_countries_header.countries_launched"
              />
            </div>

            <div>
              <StatusRow
                defaultLabel="Discontinued"
                marketplaces={discontinuedMarketplaces}
                translationKey="insights.country_tab.active_countries_header.countries_discontinued"
              />
            </div>
          </div>
        )}
      </div>
    </TileContent>
  );
}
